import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import { GACodes, LISTING_HEAP_IDS } from '@/components/_shared/constants';
import styles from './CreatorBanner.module.scss';
import { useBasicInfo } from '../_shared/contexts/BasicInfoContext';

interface CreatorBannerProps {
  isLoading: boolean;
  canManageEvent: boolean;
}

const getEditEventUrl = (id: string, isParentEvent: boolean, isSeries?: boolean, seriesId?: string) => {
  // For series child events, link to the parent event's manage page
  const eventId = isSeries && !isParentEvent && seriesId ? seriesId : id;
  return `/manage/events/${eventId}/details`;
};

export const CreatorBanner: React.FC<CreatorBannerProps> = ({ isLoading, canManageEvent }) => {
  const { id, isSeries, isParentEvent, seriesId } = useBasicInfo();

  const editEventUrl = useMemo(
    () => getEditEventUrl(id, isParentEvent, isSeries, seriesId),
    [id, isParentEvent, isSeries, seriesId],
  );

  if (isLoading || !canManageEvent) return null;

  const handleEditClick = () => {
    trackAnalyticsEvent({
      category: GACodes.GA_CATEGORY_LISTING,
      action: GACodes.GA_ACTION_EDIT_EVENT,
    });
  };

  return (
    <div className={styles.banner} role="status">
      <FormattedMessage
        id="Need to make some updates?"
        defaultMessage="Need to make some updates?"
        description="Creator banner prompt text shown to event organizers"
      />
      <a
        href={editEventUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
        onClick={handleEditClick}
        data-heap-id={LISTING_HEAP_IDS.CREATOR_TOP_BANNER_EDIT}
      >
        <FormattedMessage
          id="Edit event"
          defaultMessage="Edit event"
          description="Creator banner link text to edit the event"
        />
      </a>
    </div>
  );
};
